import React, { useState, ChangeEvent, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useTelegram } from '../../hooks/useTelegram'
import { useAppState } from '../../hooks/useAppState'

import '../../theme/forms1c.css'
import './InvoiceForPayment.css'

import {
    fetchOrganizations,
    ICounterparty,
    IOrganizationDetails,
    fetchContracts,
    fetchNomenclature,
    IContract,
    INomenclatureSuggestion,
    searchNomenclatureIn1C,
    sendInvoice,
    syncContracts,
} from '../../api/client'
import CounterpartyPicker from '../Counterparty/CounterpartyPicker'

interface IItem {
    id: number
    name: string
    amount: number
    price: number
    unit: string
}

// То, что человек набирает в полях. Количество и цена здесь строки:
// пустое поле должно оставаться пустым, а не превращаться в ноль
interface IItemInput {
    id: number
    name: string
    amount: string
    price: string
    unit: string
}

const EMPTY_ITEM: IItemInput = { id: 0, name: '', price: '', amount: '1', unit: 'шт' }

interface IFormData {
    // Основание печатается в шапке счета
    basis: string
    buyerName: string
    buyerInn: string
    buyerKpp: string
    buyerInd: string
    buyerAddress: string
    buyerPhone: string | null
    items: IItem[]
    fromFile: boolean
}

interface IFormErrors {
    buyer: string
    buyerName: string
    buyerInn: string
    buyerKpp: string
    buyerInd: string
    buyerAddress: string
    items: string
}

type TPermittedOperations = 'none' | 'add' | 'edit'

// Счет выставляется от организации базы контрагенту из нее же: обе стороны
// берутся из 1С, поэтому реквизиты в печатной форме заведомо совпадают
// с учетом. Ручной ввод покупателя оставлен для разовых сделок
const InvoiceForPayment = () => {
    const { onClose, showMainButton, hasMainButton } = useTelegram()
    const { organization } = useAppState()

    const navigate = useNavigate()
    const [search] = useSearchParams()
    const fromFile = search.get('fromFile')

    const [errors, setErrors] = useState<IFormErrors>({} as IFormErrors)
    const [sendError, setSendError] = useState<string>('')

    // Поставщик - организация из базы
    const [organizations, setOrganizations] = useState<IOrganizationDetails[]>([])
    const [supplierId, setSupplierId] = useState<number>(0)

    // Покупатель - контрагент из базы либо ручной ввод
    const [buyer, setBuyer] = useState<ICounterparty | null>(null)
    const [manual, setManual] = useState<boolean>(false)

    const [formData, setFormData] = useState<IFormData>({
        basis: '',
        buyerName: '',
        buyerInn: '',
        buyerKpp: '',
        buyerInd: '',
        buyerAddress: '',
        buyerPhone: null,
        items: [],
        fromFile: fromFile ? fromFile === '1' : false,
    })

    // Договоры покупателя: основной приезжает вместе со справочниками,
    // остальные догружаются по кнопке
    const [contracts, setContracts] = useState<IContract[]>([])
    const [contractId, setContractId] = useState<number>(0)
    const [contractsComplete, setContractsComplete] = useState(true)
    const [contractsNotice, setContractsNotice] = useState('')

    // Подсказки номенклатуры: список собран из документов за полгода,
    // поэтому ходовые услуги оказываются вверху
    const [hints, setHints] = useState<INomenclatureSuggestion[]>([])
    const [hintsOpen, setHintsOpen] = useState(false)
    const [hintNotice, setHintNotice] = useState('')

    const [selectedItem, setSelectedItem] = useState<IItem>({} as IItem)
    const [newItem, setNewItem] = useState<IItemInput>(EMPTY_ITEM)
    const [permittedOperations, setPermittedOperations] = useState<TPermittedOperations>('none')

    const supplier = organizations.find(o => o.id === supplierId) || null
    // Счет выбирает пользователь: у организации их бывает несколько,
    // и покупатель должен платить именно на нужный
    const [accountNumber, setAccountNumber] = useState<string>('')
    const account = supplier?.accounts.find(a => a.account === accountNumber)
        || supplier?.accounts.find(a => a.isDefault)
        || supplier?.accounts[0]
        || null

    useEffect(() => {
        fetchOrganizations()
            .then((list) => {
                setOrganizations(list)
                // Подставляем ту организацию, что выбрана в шапке приложения
                const preferred = list.find(o => o.id === organization?.id) || list.find(o => o.isDefault) || list[0]
                if (preferred) {
                    setSupplierId(preferred.id)
                    const first = preferred.accounts.find(a => a.isDefault) || preferred.accounts[0]
                    setAccountNumber(first ? first.account : '')
                }
            })
            .catch(e => setSendError(e instanceof Error ? e.message : String(e)))
    }, [organization])

    const loadContracts = useCallback(async (counterpartyId: number) => {
        if (!counterpartyId || !supplierId) return

        try {
            const data = await fetchContracts(counterpartyId, supplierId)
            setContracts(data.items)
            setContractsComplete(data.complete)
            // Основной договор подставляем сам: чаще всего нужен именно он
            const main = data.items.find(c => c.isMain) || data.items[0]
            setContractId(main ? main.id : 0)
        } catch {
            setContracts([])
            setContractId(0)
        }
    }, [supplierId])

    const selectBuyer = (item: ICounterparty) => {
        setBuyer(item)
        setErrors(prev => ({ ...prev, buyer: '' }))
        setContractsNotice('')
        loadContracts(item.id)
    }

    // Догрузка остальных договоров: 1С отвечает следующим опросом,
    // поэтому список перечитываем с задержкой
    const requestContracts = async () => {
        if (!buyer) return

        try {
            setContractsNotice(await syncContracts(buyer.id, supplierId))
            window.setTimeout(() => loadContracts(buyer.id), 6000)
        } catch (e) {
            setContractsNotice(e instanceof Error ? e.message : String(e))
        }
    }

    const resetBuyer = () => {
        setBuyer(null)
        setManual(false)
        setContracts([])
        setContractId(0)
        setContractsNotice('')
    }

    const handleItemNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setNewItem({ ...newItem, name: e.target.value })
        setHintsOpen(true)
        setHintNotice('')
    }

    // Подсказку выбрали: единица и цена приезжают из 1С, набирать их не нужно
    const applyHint = (hint: INomenclatureSuggestion) => {
        setNewItem({
            ...newItem,
            name: hint.name,
            unit: hint.unit || 'шт',
            price: hint.price === null ? newItem.price : String(hint.price),
        })
        setHintsOpen(false)
    }

    // Позиции нет среди использованных - просим 1С поискать по справочнику
    const askOneC = async () => {
        try {
            setHintNotice(await searchNomenclatureIn1C(supplierId, newItem.name.trim()))
            // Ответ приезжает следующим опросом очереди
            window.setTimeout(() => setHintsOpen(true), 6000)
        } catch (e) {
            setHintNotice(e instanceof Error ? e.message : String(e))
        }
    }
    // Храним ввод строкой: Number('') превращался в 0, поле само собой
    // заполнялось нулем, и стереть его было невозможно
    const handleItemAmountChange = (e: ChangeEvent<HTMLInputElement>) => setNewItem({ ...newItem, amount: e.target.value })
    const handleItemPriceChange = (e: ChangeEvent<HTMLInputElement>) => setNewItem({ ...newItem, price: e.target.value })

    const handleBack = (): void => {
        navigate(-1)
    }

    const handleSelectedItem = (item: IItem) => {
        if (item.id === selectedItem.id) {
            setSelectedItem({} as IItem)
        } else {
            setSelectedItem(item)
        }
    }

    const handleAddNewItem = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        e.stopPropagation()
        const amount = Number(String(newItem.amount).replace(',', '.'))
        const price = Number(String(newItem.price).replace(',', '.'))

        if (!newItem.name.trim() || !(amount > 0) || !(price > 0)) {
            setErrors(prev => ({ ...prev, items: 'Укажите наименование, количество и цену' }))

            return
        }

        const lastId = formData.items.length ? formData.items[formData.items.length - 1].id : 0
        const copyItems = [...formData.items]
        copyItems.push({ id: lastId + 1, name: newItem.name.trim(), amount, price, unit: newItem.unit || 'шт' })
        setFormData({ ...formData, items: copyItems })
        setErrors(prev => ({ ...prev, items: '' }))
        setNewItem(EMPTY_ITEM)
        setHintsOpen(false)
    }

    const handleEditNewItem = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        e.stopPropagation()
        const amount = Number(String(newItem.amount).replace(',', '.'))
        const price = Number(String(newItem.price).replace(',', '.'))

        const copyItems = formData.items.map(i => (
            i.id === selectedItem.id
                ? { ...i, name: newItem.name.trim(), amount, price, unit: newItem.unit || 'шт' }
                : i
        ))
        setFormData({ ...formData, items: copyItems })
    }

    const handleRemoveNewItem = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        e.stopPropagation()
        setFormData({ ...formData, items: formData.items.filter(i => i.id !== selectedItem.id) })
        setNewItem(EMPTY_ITEM)
        setHintsOpen(false)
    }

    const handleTextInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ): void => {
        const { id, value } = e.target
        setFormData(prev => ({ ...prev, [id]: value }))

        if (errors[id as keyof IFormErrors]) {
            setErrors(prev => ({ ...prev, [id]: '' }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: IFormErrors = {} as IFormErrors

        if (!supplier) newErrors.buyer = 'Не выбрана организация-поставщик'
        if (!buyer && !manual) newErrors.buyer = 'Не выбран покупатель'

        if (manual) {
            if (!formData.buyerName.trim()) newErrors.buyerName = 'Наименование обязательно к заполнению'
            if (!formData.buyerInn.trim()) newErrors.buyerInn = 'ИНН обязателен к заполнению'
            if (!formData.buyerAddress.trim()) newErrors.buyerAddress = 'Адрес обязателен к заполнению'
        }

        if (!formData.fromFile && !formData.items.length) {
            newErrors.items = 'Нет ни одной строчки товаров/услуг'
        }

        setErrors(newErrors)

        return Object.keys(newErrors).length === 0
    }

    const onSendData = useCallback(async () => {
        if (!validateForm()) return

        setSendError('')
        try {
            await sendInvoice({
                organizationId: supplierId,
                account: account ? account.account : undefined,
                contractId: contractId || undefined,
                counterpartyId: buyer ? buyer.id : 0,
                fromFile: formData.fromFile,
                basis: formData.basis,
                items: formData.items.map(i => ({ name: i.name, amount: i.amount, price: i.price, unit: i.unit || 'шт' })),
                buyerName: buyer ? buyer.name : formData.buyerName,
                buyerInn: buyer ? (buyer.inn || '') : formData.buyerInn,
                buyerKpp: buyer ? (buyer.kpp || '') : formData.buyerKpp,
                buyerInd: buyer ? '' : formData.buyerInd,
                buyerAddress: buyer ? (buyer.address || '') : formData.buyerAddress,
                buyerPhone: buyer ? buyer.phone : formData.buyerPhone,
            })
            onClose()
        } catch (e) {
            setSendError(e instanceof Error ? e.message : String(e))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, supplierId, buyer, manual, account, contractId])

    // Подсказки ищем на сервере: номенклатура лежит у нас, в 1С не ходим
    useEffect(() => {
        if (!supplierId || !hintsOpen) return

        const text = newItem.name.trim()
        const timer = window.setTimeout(() => {
            fetchNomenclature(supplierId, text, buyer ? buyer.id : undefined)
                .then(setHints)
                .catch(() => setHints([]))
        }, 300)

        return () => window.clearTimeout(timer)
    }, [newItem.name, supplierId, buyer, hintsOpen])

    useEffect(() => {
        if (selectedItem.id === undefined) return

        setNewItem({
            id: selectedItem.id,
            name: selectedItem.name,
            unit: selectedItem.unit,
            amount: String(selectedItem.amount ?? ''),
            price: String(selectedItem.price ?? ''),
        })
    }, [selectedItem])

    useEffect(() => {
        const amount = Number(String(newItem.amount).replace(',', '.'))
        const price = Number(String(newItem.price).replace(',', '.'))

        if (!(amount > 0) || !(price > 0) || !newItem.name.trim().length) {
            setPermittedOperations('none')
        } else {
            setPermittedOperations(newItem.id ? 'edit' : 'add')
        }
    }, [newItem])

    useEffect(() => {
        if (formData.items.length) setErrors(prev => ({ ...prev, items: '' }))
    }, [formData.items.length])

    // Отправка по главной кнопке Telegram
    useEffect(() => {
        return showMainButton('Выставить счет', onSendData)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSendData])

    const total = formData.items.reduce((sum, i) => sum + i.amount * i.price, 0)

    return (
        <div className="form-container">
            <div className="form-header">
                <button
                    type="button"
                    className="back-button"
                    onClick={handleBack}
                    aria-label="Назад"
                >
                    ‹
                </button>
                <h1>📋 Создание <strong>счета</strong></h1>
                <p>Поставщик и покупатель берутся из базы 1С</p>
            </div>

            <form className="adaptive-form">
                <fieldset className="form-section">
                    <legend>🏢 Поставщик</legend>

                    <div className="input-group">
                        <label htmlFor="supplier" className="required">Организация</label>
                        <select
                            id="supplier"
                            value={supplierId}
                            onChange={(e) => {
                                const id = Number(e.target.value)
                                setSupplierId(id)
                                const next = organizations.find(o => o.id === id)
                                const first = next?.accounts.find(a => a.isDefault) || next?.accounts[0]
                                setAccountNumber(first ? first.account : '')
                            }}
                        >
                            {!organizations.length && <option value={0}>Организации не загружены</option>}
                            {organizations.map(o => (
                                <option key={o.id} value={o.id}>{o.name} · ИНН {o.inn}</option>
                            ))}
                        </select>
                    </div>

                    {/* Расчетный счет выбирает пользователь: на него покупатель
                        и будет платить, и его реквизиты попадут в шапку счета */}
                    {supplier && supplier.accounts.length > 0 && (
                        <div className="input-group">
                            <label htmlFor="supplierAccount" className="required">Расчётный счёт</label>
                            <select
                                id="supplierAccount"
                                value={account ? account.account : ''}
                                onChange={(e) => setAccountNumber(e.target.value)}
                            >
                                {supplier.accounts.map(a => (
                                    <option key={a.account} value={a.account}>
                                        {a.account}{a.bankName ? ` · ${a.bankName}` : ''}
                                    </option>
                                ))}
                            </select>
                            <span className="hint">На этот счёт покупатель перечислит оплату</span>
                        </div>
                    )}

                    {supplier && !supplier.accounts.length && (
                        <div className="error-message">
                            У организации нет расчётного счёта в 1С — счёт выставить не с чего
                        </div>
                    )}

                    {/* Показываем реквизиты, которые уйдут в счет: их правка -
                        в 1С, поэтому здесь они только для проверки */}
                    {supplier && (
                        <div className="party-card">
                            <div className="party-line">ИНН {supplier.inn}{supplier.kpp ? ` · КПП ${supplier.kpp}` : ''}</div>
                            {supplier.address && <div className="party-line muted">{supplier.address}</div>}
                            {account
                                ? (
                                    <div className="party-line muted">
                                        Р/с {account.account}
                                        {account.bankName ? ` · ${account.bankName}` : ''}
                                        {account.bik ? ` · БИК ${account.bik}` : ''}
                                    </div>
                                )
                                : <div className="party-line error-message">В 1С нет банковского счета - счет выставить не получится</div>
                            }
                        </div>
                    )}
                </fieldset>

                <fieldset className="form-section">
                    <legend>👤 Покупатель</legend>

                    {/* В ручном режиме поиск не нужен: покупателя вписывают
                        полями ниже */}
                    {!manual && (
                        <CounterpartyPicker
                            organizationId={supplierId}
                            selected={buyer}
                            onSelect={selectBuyer}
                            onClear={resetBuyer}
                            label="Найдите покупателя по названию или ИНН"
                            error={errors.buyer}
                        />
                    )}

                    {manual && (
                        <>
                            <div className="input-group">
                                <label htmlFor="buyerName" className="required">Наименование</label>
                                <input
                                    id="buyerName"
                                    type="text"
                                    value={formData.buyerName}
                                    onChange={handleTextInputChange}
                                    placeholder="ООО Техно"
                                    className={errors.buyerName ? 'error' : ''}
                                />
                                {errors.buyerName && <span className="error-message">{errors.buyerName}</span>}
                            </div>

                            <div className="input-row">
                                <div className="input-group half">
                                    <label htmlFor="buyerInn" className="required">ИНН</label>
                                    <input
                                        id="buyerInn"
                                        type="text"
                                        inputMode="numeric"
                                        value={formData.buyerInn}
                                        onChange={handleTextInputChange}
                                        placeholder="ИНН"
                                        className={errors.buyerInn ? 'error' : ''}
                                    />
                                    {errors.buyerInn && <span className="error-message">{errors.buyerInn}</span>}
                                </div>
                                <div className="input-group half">
                                    <label htmlFor="buyerKpp">КПП</label>
                                    <input
                                        id="buyerKpp"
                                        type="text"
                                        inputMode="numeric"
                                        value={formData.buyerKpp}
                                        onChange={handleTextInputChange}
                                        placeholder="КПП"
                                    />
                                </div>
                            </div>

                            <div className="input-row">
                                <div className="input-group half">
                                    <label htmlFor="buyerInd">Индекс</label>
                                    <input
                                        id="buyerInd"
                                        type="text"
                                        inputMode="numeric"
                                        value={formData.buyerInd}
                                        onChange={handleTextInputChange}
                                        placeholder="Индекс"
                                    />
                                </div>
                                <div className="input-group half">
                                    <label htmlFor="buyerPhone">Телефон</label>
                                    <input
                                        id="buyerPhone"
                                        type="text"
                                        value={formData.buyerPhone || ''}
                                        onChange={handleTextInputChange}
                                        placeholder="+7 (999) 999-99-99"
                                    />
                                </div>
                            </div>

                            <div className="input-group">
                                <label htmlFor="buyerAddress" className="required">Адрес</label>
                                <input
                                    id="buyerAddress"
                                    type="text"
                                    value={formData.buyerAddress}
                                    onChange={handleTextInputChange}
                                    placeholder="Адрес"
                                    className={errors.buyerAddress ? 'error' : ''}
                                />
                                {errors.buyerAddress && <span className="error-message">{errors.buyerAddress}</span>}
                            </div>

                            <button type="button" className="tg-button secondary" onClick={resetBuyer}>
                                Выбрать из базы
                            </button>
                        </>
                    )}

                    {!manual && !buyer && (
                        <button type="button" className="tg-button secondary" onClick={() => setManual(true)}>
                            Ввести вручную
                        </button>
                    )}
                </fieldset>

                <fieldset className="form-section">
                    <legend>📑 Основание</legend>

                    {/* Договор берем из 1С: основной подставляется сам,
                        остальные догружаются по кнопке - их бывает много,
                        а нужны они редко */}
                    {buyer && contracts.length > 0 && (
                        <div className="input-group">
                            <label htmlFor="contract">Договор из 1С</label>
                            <select
                                id="contract"
                                value={contractId}
                                onChange={(e) => setContractId(Number(e.target.value))}
                            >
                                <option value={0}>Без договора</option>
                                {contracts.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.title}{c.isMain ? ' · основной' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {buyer && !contractsComplete && (
                        <div className="input-group">
                            <button type="button" className="tg-button secondary" onClick={requestContracts}>
                                {contracts.length ? 'Показать остальные договоры' : 'Запросить договоры из 1С'}
                            </button>
                            <span className="hint">
                                Загружен только основной договор. Остальные запросим у 1С — это занимает несколько секунд
                            </span>
                        </div>
                    )}

                    {contractsNotice && <div className="notice">{contractsNotice}</div>}

                    {/* Текстом основание задают, когда договора в 1С нет */}
                    {!contractId && (
                        <div className="input-group">
                            <label htmlFor="basis">Или впишите основание вручную</label>
                            <input
                                id="basis"
                                type="text"
                                value={formData.basis}
                                onChange={handleTextInputChange}
                                placeholder="Счёт-оферта, разовая поставка"
                            />
                            <span className="muted">Если оставить пустым, в счете будет «Без договора»</span>
                        </div>
                    )}
                </fieldset>

                {!formData.fromFile &&
                    <fieldset className="form-section">
                        <legend>📄 Список работ/услуг</legend>
                        <div className="input-group">
                            {formData.items.length
                                ? (
                                    <div className="priority-buttons" style={{ flexDirection: 'column' }}>
                                        {formData.items.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={`priority-btn ${selectedItem.id === item.id ? 'active' : ''}`}
                                                onClick={() => handleSelectedItem(item)}
                                            >
                                                {item.name} · {item.amount} {item.unit} × {item.price}
                                            </button>
                                        ))}
                                    </div>
                                )
                                : null
                            }
                            {errors.items && <span className="error-message">{errors.items}</span>}
                        </div>

                        <div className="input-group">
                            <input
                                id="itemName"
                                type="text"
                                value={newItem.name}
                                onChange={handleItemNameChange}
                                onFocus={() => setHintsOpen(true)}
                                placeholder="Наименование товара/услуги"
                                autoComplete="off"
                            />

                            {/* Подсказки из 1С: частые услуги вверху, цена
                                подставляется по последней продаже */}
                            {hintsOpen && hints.length > 0 && (
                                <div className="search-results">
                                    {hints.map(hint => (
                                        <button
                                            key={hint.id}
                                            type="button"
                                            className="search-item"
                                            onClick={() => applyHint(hint)}
                                        >
                                            <span>
                                                {hint.name}
                                                <span className="tile-hint" style={{ display: 'block' }}>
                                                    {hint.unit || 'шт'}
                                                    {hint.price !== null
                                                        ? ` · ${hint.price.toLocaleString('ru-RU')} ₽${hint.priceForBuyer ? ' для этого покупателя' : ''}`
                                                        : ''}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {hintsOpen && newItem.name.trim().length >= 3 && !hints.length && (
                                <>
                                    <span className="hint">Среди недавних позиций такого нет</span>
                                    <button type="button" className="tg-button secondary" onClick={askOneC}>
                                        Поискать в справочнике 1С
                                    </button>
                                </>
                            )}

                            {hintNotice && <span className="hint">{hintNotice}</span>}
                        </div>

                        <div className="input-row">
                            <div className="input-group half">
                                <label htmlFor="itemAmount" className="required">Количество</label>
                                <input
                                    id="itemAmount"
                                    type="number"
                                    value={newItem.amount}
                                    onChange={handleItemAmountChange}
                                    placeholder="Количество"
                                    min={1}
                                    step="0.01"
                                />
                            </div>
                            <div className="input-group half">
                                <label htmlFor="itemUnit">Ед. изм.</label>
                                <input
                                    id="itemUnit"
                                    type="text"
                                    value={newItem.unit}
                                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                                    placeholder="шт"
                                />
                            </div>
                        </div>

                        <div className="input-row">
                            <div className="input-group half">
                                <label htmlFor="itemPrice" className="required">Цена</label>
                                <input
                                    id="itemPrice"
                                    type="number"
                                    min={0.01}
                                    step="0.01"
                                    value={newItem.price}
                                    onChange={handleItemPriceChange}
                                    placeholder="Цена"
                                />
                            </div>
                        </div>

                        <div className="input-group" style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                            {permittedOperations !== 'none' &&
                                <input
                                    type="button"
                                    id="buttonAddItem"
                                    className="tg-button secondary"
                                    style={{ fontSize: '14px' }}
                                    onClick={handleAddNewItem}
                                    value="Добавить"
                                />
                            }
                            {permittedOperations === 'edit' &&
                                <>
                                    <input
                                        id="buttonEditItem"
                                        type="button"
                                        className="tg-button primary"
                                        style={{ fontSize: '14px' }}
                                        onClick={handleEditNewItem}
                                        value="Изменить"
                                    />
                                    <input
                                        id="buttonRemoveItem"
                                        type="button"
                                        className="tg-button danger"
                                        style={{ fontSize: '14px' }}
                                        onClick={handleRemoveNewItem}
                                        value="Удалить"
                                    />
                                </>
                            }
                        </div>

                        {total > 0 && <p className="muted">Итого: {total.toLocaleString('ru-RU')} ₽</p>}
                    </fieldset>
                }

                {errors.buyer && !manual && !buyer && <span className="error-message">{errors.buyer}</span>}
                {sendError && <span className="error-message">{sendError}</span>}

                {/* В Telegram отправку делает родная кнопка внизу окна -
                    своя нужна только в браузере */}
                {!hasMainButton && (
                    <button type="button" className="tg-button primary" onClick={onSendData}>
                        Выставить счет
                    </button>
                )}
            </form>
        </div>
    )
}

export default InvoiceForPayment
